import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Beer, Utensils, QrCode } from 'lucide-react';

const MenuBoard = () => {
  const { pixQRCodeUrl } = useTheme();
  const [menuData, setMenuData] = useState(null);

  useEffect(() => {
    // Busca os dados configurados na mesa de controlo
    const stored = localStorage.getItem('cabrones_event_menu');
    if (stored) {
      setMenuData(JSON.parse(stored));
    }

    // Atualiza o ecrã em tempo real se a mesa de controlo for alterada
    const handleStorageChange = (e) => {
      if (e.key === 'cabrones_event_menu' && e.newValue) {
        setMenuData(JSON.parse(e.newValue));
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  if (!menuData) {
    return (
      <div className="h-screen w-screen bg-[#09090b] text-white flex items-center justify-center p-8 overflow-hidden">
        <h1 className="text-3xl md:text-4xl font-black uppercase tracking-widest text-muted-foreground">
          Nenhum cardápio configurado.
        </h1>
      </div>
    );
  }

  // Lógica para dividir bebidas em 2 colunas se houver muitas, evitando quebra de tela
  const drinkCount = menuData.drinks.length;
  const drinkGridCols = drinkCount > 6 ? 'grid-cols-2' : 'grid-cols-1';

  return (
    <div className="h-screen w-screen bg-[#09090b] text-white p-4 flex flex-col font-sans overflow-hidden box-border">
      {/* CABEÇALHO COM A LOGO DO CLUBE */}
      <header className="shrink-0 flex justify-center items-center gap-4 py-2 border-b-2 border-primary/50 mb-4">
        <img 
          src="/logo.png" 
          alt="Escudo Cabrones MC" 
          className="h-12 md:h-16 object-contain drop-shadow-[0_0_15px_rgba(250,204,21,0.5)]" 
        />
        <h1 className="text-5xl md:text-6xl font-black uppercase tracking-tighter text-primary drop-shadow-lg">
          Cabrones MC
        </h1>
      </header>

      {/* CORPO PRINCIPAL (Flex-1 e min-h-0 garantem que não empurre a tela para baixo) */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        
        {/* REFEIÇÃO DO DIA */}
        <div className="flex flex-col h-full border-2 border-border/30 rounded-2xl p-4 md:p-6 bg-card/10 backdrop-blur-sm overflow-hidden">
          <div className="shrink-0 flex items-center gap-3 mb-4 border-b border-border/50 pb-3">
            <Utensils className="w-8 h-8 md:w-10 md:h-10 text-primary" />
            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-widest text-primary">
              Para Comer
            </h2>
          </div>
          
          <div className="flex-1 min-h-0 flex flex-col justify-center items-center text-center space-y-6 md:space-y-8">
            <h3 className="text-5xl md:text-6xl font-black uppercase text-white leading-tight break-words max-w-full">
              {menuData.meal.name}
            </h3>
            <div className="inline-block bg-primary text-primary-foreground px-6 md:px-8 py-3 md:py-4 rounded-xl transform -rotate-2 shadow-xl">
              <span className="text-4xl md:text-5xl font-black font-mono drop-shadow-md">
                R$ {menuData.meal.price.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* BEBIDAS SELECIONADAS */}
        <div className="flex flex-col h-full border-2 border-border/30 rounded-2xl p-4 md:p-6 bg-card/10 backdrop-blur-sm overflow-hidden">
          <div className="shrink-0 flex items-center gap-3 mb-4 border-b border-border/50 pb-3">
            <Beer className="w-8 h-8 md:w-10 md:h-10 text-primary" />
            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-widest text-primary">
              Para Beber
            </h2>
          </div>

          <div className={`flex-1 min-h-0 grid ${drinkGridCols} gap-x-6 gap-y-3 content-center overflow-hidden`}>
            {menuData.drinks.length > 0 ? (
              menuData.drinks.map((drink, index) => (
                <div key={index} className="flex items-center justify-between border-b border-border/20 pb-2">
                  <span className="text-xl md:text-2xl font-bold uppercase tracking-wide text-white truncate pr-3">
                    {drink.name}
                  </span>
                  <span className="text-xl md:text-2xl font-black font-mono text-primary shrink-0">
                    R$ {drink.price.toFixed(2)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-lg md:text-xl text-muted-foreground uppercase text-center w-full col-span-full">Nenhuma bebida listada.</p>
            )}
          </div>
        </div>
      </div>

      {/* RODAPÉ E PIX (Fixo embaixo) */}
      <footer className="shrink-0 mt-4 pt-4 border-t-2 border-primary/50 flex items-center justify-between">
        <div className="flex-1 flex items-center gap-4">
          <QrCode className="w-10 h-10 md:w-14 md:h-14 text-primary" />
          <div>
            <p className="text-xl md:text-2xl font-black uppercase tracking-widest text-white">Pague no PIX</p>
            <p className="text-sm md:text-base font-mono text-muted-foreground uppercase mt-1">Aponte a câmera para o QR Code</p>
          </div>
        </div>
        
        {pixQRCodeUrl ? (
          <div className="bg-white p-2 rounded-xl shrink-0 shadow-lg">
            <img src={pixQRCodeUrl} alt="PIX QR Code" className="w-20 h-20 md:w-28 md:h-28 object-contain" />
          </div>
        ) : (
          <div className="w-20 h-20 md:w-28 md:h-28 border-2 border-dashed border-muted-foreground rounded-xl flex items-center justify-center shrink-0">
            <span className="text-xs uppercase text-muted-foreground text-center p-2">Sem QR Code</span>
          </div>
        )}
      </footer>
    </div>
  );
};

export default MenuBoard;
